/**
 * Whether the Assistant tab is on (outlook-orachat-uplift s5, design C11/C12).
 *
 * The switch is the Product API App Configuration key
 * `OutlookAddin:Assistant:Enabled`, served by `GET /api/workflows/addin/settings`
 * (through `orchestratorApi`'s request path: bearer, surface stamp, relay). It is
 * not a feature flag and not a `VITE_*` key: the operator flips it per
 * environment, and an open pane follows on its next focus.
 *
 * Off unless the API positively says `assistantEnabled: true`. A failed read, a
 * missing field or anything that is not the boolean `true` is off, which is
 * today's pane exactly: no switch row, no chat chunk loaded.
 *
 * The focus / visibility RE-reads are background reads: they never open the
 * sign-in dialog (they run only while the chat's silent token is good; with no
 * silent token there is no read and the value stands). A re-read that runs
 * follows the first-load rule: unreadable is off (G1-4).
 */

import { useEffect, useState } from "react";

import { getChatToken } from "../oraChat/bootOraChat";
import { orchestratorApi } from "../services/orchestratorApi";

let settleFirstSetting: (enabled: boolean) => void = () => undefined;
let firstSetting: Promise<boolean> = newFirstSetting();

function newFirstSetting(): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    settleFirstSetting = resolve;
  });
}

/**
 * Resolves with the switch's first value once a pane has APPLIED it (on or
 * off). The proof harness waits on it, so `ready` means the settled header
 * and switch row (capture contract §2). The pane also waits on it so a
 * resume-intent tab is not closed by the switch's initial off (AD-25).
 */
export function firstAssistantSetting(): Promise<boolean> {
  return firstSetting;
}

/**
 * True once `firstAssistantSetting()` has resolved. The read happens inside
 * the effect, never at module load, so a test reset is seen.
 */
export function useFirstAssistantSettingSettled(): boolean {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    let alive = true;
    void firstAssistantSetting().then(() => {
      if (alive) setSettled(true);
    });
    return () => {
      alive = false;
    };
  }, []);
  return settled;
}

/** Test helper: a fresh, unsettled first setting. */
export function _resetFirstAssistantSettingForTests(): void {
  firstSetting = newFirstSetting();
}

/** One read of the switch. Never throws: any failure is off. */
export async function readAssistantEnabled(): Promise<boolean> {
  try {
    const settings = await orchestratorApi.getAddinSettings();
    return settings?.assistantEnabled === true;
  } catch {
    return false;
  }
}

/**
 * The switch as React state: read on mount and again whenever the pane regains
 * focus or becomes visible. Starts off; a slower stale read never overwrites a
 * newer one. A re-read runs only while the silent token is good (so it can never
 * open the sign-in dialog) and, like the first read, a failed one is off. Every
 * read that runs applies its value, so the first setting always settles, even
 * when a re-read overtakes the first read and fails (r2 CR-1).
 */
export function useAssistantEnabled(): boolean {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let alive = true;
    let latest = 0;
    const apply = (seq: number, value: boolean) => {
      if (!alive || seq !== latest) return;
      setEnabled(value);
      settleFirstSetting(value); // a no-op after the first
    };
    const read = () => {
      const seq = ++latest;
      void readAssistantEnabled().then((value) => apply(seq, value));
    };
    const reread = () => {
      getChatToken().then(
        () => {
          const seq = ++latest;
          void readAssistantEnabled().then((value) => apply(seq, value));
        },
        () => {
          // No silent token (signed out / expired): no read, so no dialog.
        },
      );
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") reread();
    };

    read();
    window.addEventListener("focus", reread);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      alive = false;
      window.removeEventListener("focus", reread);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return enabled;
}
